<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">
          <template v-if="isIndentList">
            {{ $t('service.addNewIndentService') }}
          </template>
          <template v-else>
            {{ $t('service.addNewService') }}
          </template>
        </h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push(backUrl)"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('service.addService') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs
          v-if="!loadError && allDataLoaded"
          card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="service-new-form" tag="form">
              <d-row>
                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.servicesGroup') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="group-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="group_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="groupOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceType') }}:
                    </label>

                    <validation-provider
                      rules="required|non_zero"
                      name="type-id"
                      v-slot="{ errors }">
                      <d-form-select
                        v-model="type_id"
                        :state="errors.length ? 'invalid' : null"
                        :options="typeOptions" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.fromHowManyPiecesThePriceIsMultiplied') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="price-multiple-from"
                      v-slot="{ errors }">
                      <d-input
                        v-model="price_multiple_from"
                        type="number"
                        min="0"
                        step="1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row v-if="isIndentList">
                <d-col cols="4">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.daysToSample') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0"
                      name="days-to-sample"
                      v-slot="{ errors }">
                      <d-input
                        v-model="days_to_sample"
                        type="number"
                        min="0"
                        step="1"
                        :state="errors.length ? 'invalid' : null" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div
                    v-if="isIndentList"
                    class="form-group">
                    <label for="days_no_sample_indent">
                      {{ $t('service.daysNoSampleIndent') }}
                    </label>

                    <validation-provider
                      rules="only-integer-numbers|min_value:0"
                      name="days_no_sample_indent"
                      v-slot="{ errors }">
                      <d-input-group
                        :prepend="$t('service.daysNoSampleIndentPrepend')"
                        :append="$t('service.daysNoSampleIndentAppend')"
                        :class="{
                          'w-100': true,
                          'is-invalid': errors.length
                        }">
                        <input
                          :class="{
                            'form-control': true,
                            'is-invalid': errors.length,
                            'text-center': true
                          }"
                          :invalid="errors.length ? true : null"
                          type="number"
                          v-model="days_no_sample_indent" />
                      </d-input-group>
                      <d-form-invalid-feedback>
                        {{ errors[0] }}
                      </d-form-invalid-feedback>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="8">
                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceName') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="name"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="name" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.serviceCode') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="code"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label for="name">
                      {{ $t('service.catalogUrl') }}:
                    </label>

                    <validation-provider
                      rules="valid-url"
                      name="catalog_url"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="catalog_url" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="available_in_embriodery"
                      toggle
                      toggle-small
                      class="mt-2 mb-0">
                      {{ $t('service.availableInHafciarnia') }}
                    </d-checkbox>
                  </div>

                  <div class="form-group">
                    <d-checkbox
                      inline
                      v-model="disable_for_customer"
                      toggle
                      toggle-small
                      class="mt-2 mb-0">
                      {{ $t('product.disableForCustomer') }}
                    </d-checkbox>
                  </div>

                  <div class="form-group">
                    <label>
                      {{ $t('ui.shortDescription') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="intro"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        v-model="intro" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>

                  <div class="form-group">
                    <label>
                      {{ $t('service.serviceDescription') }}:
                    </label>

                    <validation-provider
                      rules="required"
                      name="description"
                      v-slot="{ errors }">
                      <d-textarea
                        :state="errors.length ? 'invalid' : null"
                        v-model="description"
                        :rows="8" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="4">
                  <d-card class="p-3">
                    <div class="form-group">
                      <label for="name">
                        {{ $t('service.stitchNumber') }}:
                      </label>

                      <d-row>
                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.from')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="stitch_number_from" />
                          </d-input-group>
                        </d-col>

                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.to')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="stitch_number_to" />
                          </d-input-group>
                        </d-col>
                      </d-row>
                    </div>

                    <div class="form-group">
                      <label for="name">
                        {{ $t('service.height') }}:
                      </label>

                      <d-row>
                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.from')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="height_from" />
                          </d-input-group>
                        </d-col>

                        <d-col cols="6">
                          <d-input-group
                            :prepend="$t('ui.to')">
                            <d-input
                              type="number"
                              class="text-center"
                              v-model="height_to" />
                          </d-input-group>
                        </d-col>
                      </d-row>
                    </div>
                  </d-card>
                </d-col>
              </d-row>
            </validation-observer>
          </d-tab>
          <d-tab :title="$t('service.servicePrices')">
            <p class="text-center">
              {{ $t('service.servicePricesVisibleAfterSave') }}
            </p>
          </d-tab>

          <d-tab :title="$t('service.setupPrices')">
            <p class="text-center">
              {{ $t('service.setupPricesVisibleAfterSave') }}
            </p>
          </d-tab>

          <d-tab :title="$t('settings.transaltion.translations')">
            <p class="text-center">
              {{ $t('service.addingTransialtionsAvailableAfterSave') }}
            </p>
          </d-tab>
        </d-tabs>

        <div v-if="!allDataLoaded" class="p-4 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'service-new',
  computed: {
    allDataLoaded () {
      return this.loadedGroups && this.loadedTypes;
    },
    backUrl () {
      if (this.isIndentList) {
        return '/zdobienia-indent';
      }

      return '/zdobienia';
    },
    groupOptions () {
      return [{ value: 0, text: this.$t('service.serviceWithoutGroup') }].concat(this.groups.map(group => ({
        value: group.id,
        text: group.name
      })));
    },
    typeOptions () {
      return [{ value: 0, text: this.$t('service.selectServiceType') }].concat(this.types.map(type => ({
        value: type.id,
        text: type.name
      })));
    },
    isIndentList () {
      return this.$route.path.indexOf('zdobienia-indent') > -1;
    }
  },
  data() {
    return {
      available_in_embriodery: false,
      group_id: 0,
      type_id: 0,
      name: '',
      code: '',
      intro: '',
      catalog_url: '',
      price_multiple_from: 0,
      days_no_sample_indent: 0,
      disable_for_customer: false,
      days_to_sample: 4,
      description: '',
      stitch_number_from: 0,
      stitch_number_to: 0,
      height_from: 0,
      height_to: 0,
      loadedGroups: false,
      loadedTypes: false,
      loadError: false,
      groups: [],
      types: []
    };
  },
  mounted () {
    FormUtils.loadAdditionalData(this, {
      method: 'get',
      endpoint: '/services/groups/items',
      loadedKey: 'loadedGroups',
      outputKey: 'groups',
      errorKey: 'loadError'
    });

    FormUtils.loadAdditionalData(this, {
      method: 'get',
      endpoint: '/services/types',
      loadedKey: 'loadedTypes',
      outputKey: 'types',
      errorKey: 'loadError'
    });
  },
  methods: {
    validateForm () {
      FormUtils.validate(this.$refs['service-new-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        available_in_embriodery: +this.available_in_embriodery,
        disable_for_customer: +this.disable_for_customer,
        group_id: this.group_id,
        type_id: this.type_id,
        name: this.name,
        code: this.code,
        intro: this.intro,
        price_multiple_from: this.price_multiple_from,
        description: this.description,
        is_indent: +this.isIndentList,
        stitch_number_from: this.stitch_number_from,
        stitch_number_to: this.stitch_number_to,
        height_from: this.height_from,
        height_to: this.height_to,
        catalog_url: this.catalog_url
      };

      if (this.isIndentList) {
        formDataToSend.days_to_sample = this.days_to_sample;
        formDataToSend.days_no_sample_indent = +this.days_no_sample_indent;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/services/store',
        formData: formDataToSend,
        successTitle: this.$t('service.addServiceSuccessTitle'),
        successText: this.$t('service.addServiceSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.addServiceErrorText'),
        successAction: (responseData) => {
          if (this.isIndentList) {
            this.$router.push('/zdobienia-indent/edycja/' + parseInt(responseData.id, 10))
          } else {
            this.$router.push('/zdobienia/edycja/' + parseInt(responseData.id, 10));
          }
        }
      });
    }
  }
};
</script>
